import React from 'react'
import { View } from 'react-native'
import { useIsFocused } from '@react-navigation/native'
import { AppContext } from '@pulseops/common'
import PolicyDispatchingTopSection from './PolicyDispatchingTopSection'
import PolicyDispatchingBreadcrumb from './PolicyDispatchingBreadcrumb'
import { PolicyDispatchingStackParamList } from '../PolicyDispatchingStackParamList'

type Props = {
  children: React.ReactNode
  routeName: keyof PolicyDispatchingStackParamList
  routeParams?: unknown
  navigation: any
}

const policyDispatchingFontFamily =
  'ui-sans-serif,system-ui,sans-serif,"Apple Color Emoji","Segoe UI Emoji","Segoe UI Symbol","Noto Color Emoji"'

const policyDispatchingFontStyle = `
  .policy-dispatching-page,
  .policy-dispatching-page *,
  .policy-dispatching-page button,
  .policy-dispatching-page input,
  .policy-dispatching-page textarea,
  .policy-dispatching-page select,
  .policy-dispatching-page .MuiTypography-root,
  .policy-dispatching-page .MuiInputBase-root,
  .policy-dispatching-page .MuiInputBase-input,
  .policy-dispatching-page .MuiTableCell-root,
  .MuiPopover-root *,
  .MuiAutocomplete-popper *,
  .MuiPickersBasePicker-container *,
  .MuiPickersModal-dialogRoot * {
    font-family: ${policyDispatchingFontFamily} !important;
  }

  .policy-dispatching-page input:disabled,
  .policy-dispatching-page input[readonly],
  .policy-dispatching-page textarea:disabled,
  .policy-dispatching-page textarea[readonly],
  .policy-dispatching-page .MuiInputBase-root.Mui-disabled,
  .policy-dispatching-page .MuiSelect-root.Mui-disabled,
  .MuiDialog-root input:disabled,
  .MuiDialog-root input[readonly],
  .MuiDialog-root textarea:disabled,
  .MuiDialog-root textarea[readonly],
  .MuiDialog-root .MuiInputBase-root.Mui-disabled,
  .MuiDialog-root .MuiSelect-root.Mui-disabled {
    background-color: #f3f4f6 !important;
  }

  .policy-dispatching-page button:not(:disabled),
  .policy-dispatching-page .MuiButtonBase-root:not(.Mui-disabled),
  .policy-dispatching-page [role="button"]:not([aria-disabled="true"]),
  .MuiDialog-root button:not(:disabled),
  .MuiDialog-root .MuiButtonBase-root:not(.Mui-disabled),
  .MuiDialog-root [role="button"]:not([aria-disabled="true"]) {
    cursor: pointer !important;
    transition: transform 120ms ease, box-shadow 120ms ease, filter 120ms ease, opacity 120ms ease;
  }

  .policy-dispatching-page button:disabled,
  .policy-dispatching-page .MuiButtonBase-root.Mui-disabled,
  .policy-dispatching-page [role="button"][aria-disabled="true"],
  .MuiDialog-root button:disabled,
  .MuiDialog-root .MuiButtonBase-root.Mui-disabled,
  .MuiDialog-root [role="button"][aria-disabled="true"] {
    cursor: not-allowed !important;
  }

  .policy-dispatching-page button:not(:disabled):hover,
  .policy-dispatching-page .MuiButtonBase-root:not(.Mui-disabled):hover,
  .MuiDialog-root button:not(:disabled):hover,
  .MuiDialog-root .MuiButtonBase-root:not(.Mui-disabled):hover {
    filter: brightness(0.97);
  }

  .policy-dispatching-page button:not(:disabled):active,
  .policy-dispatching-page .MuiButtonBase-root:not(.Mui-disabled):active,
  .MuiDialog-root button:not(:disabled):active,
  .MuiDialog-root .MuiButtonBase-root:not(.Mui-disabled):active {
    transform: translateY(1px) scale(0.98);
    filter: brightness(0.92);
  }
`

export default function PolicyDispatchingLayout({ children, routeName, routeParams, navigation }: Props) {
  const { changeBreadcrumb } = React.useContext(AppContext.AppContextInstance)
  const isFocused = useIsFocused()

  React.useEffect(() => {
    if (isFocused) {
      changeBreadcrumb([
        {
          title: '',
          navigate: () => {
            navigation.replace('HomeScreen')
          }
        }
      ])
    }
  }, [])

  return (
    <>
      <style>{policyDispatchingFontStyle}</style>
      <div className="policy-dispatching-page" style={styles.page as React.CSSProperties}>
        <PolicyDispatchingTopSection />
        <View style={styles.breadcrumbContainer}>
          <PolicyDispatchingBreadcrumb routeName={routeName} routeParams={routeParams} />
        </View>
        <View style={styles.content}>{children}</View>
      </div>
    </>
  )
}

const styles: Record<string, any> = {
  page: {
    display: 'flex',
    flexDirection: 'column' as const,
    height: '100%',
    fontFamily: policyDispatchingFontFamily
  },
  content: {
    minHeight: 0,
    flex: 1
  },
  breadcrumbContainer: {
    paddingTop: 16,
    paddingRight: 40,
    paddingBottom: 0,
    paddingLeft: 40
  }
}
